import Info from '@mui/icons-material/Info';
import { Typography } from '@mui/material';
import { getRecieverFactoringRequestOnlyQuote } from 'lib/api';
import {
  fetchAllVerificationsByOperation,
  fetchVerificationsPortfolio,
  unAssignVerifierToOperations,
} from 'lib/api/documentConfirmation';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import AssignationDialog from './components/AssignationDialog';
import ShoppingDialogRecieved from './components/ShoppingDialog';

const VerificationByOperation = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [canReadAll, setCanReadAll] = useState(false);
  const [factoringRequests, setFactoringRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [miniLoading, setMiniLoading] = useState(false);
  const [openShoppingCart, setOpenShoppingCart] = useState(false);
  const [openRequest, setOpenRequest] = useState<any | null>(null);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState<any>({
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchStatus, setSearchStatus] = useState<string>('all');
  const [searchBusinessIdentifier, setSearchBusinessIdentifier] =
    useState<string>('');
  const [searchVerifierId, setSearchVerifierId] = useState<string>('all');
  const [searchVerified, setSearchVerified] = useState<string>('notVerified');
  const [searchFactoringRequestId, setSearchFactoringRequestId] =
    useState<any>(null);

  const [unassignedOperations, setUnassignedOperations] = useState<any[]>([]);
  const [executivesOptions, setExecutivesOptions] = useState<any[]>([]);
  const [openAssignationDialog, setOpenAssignationDialog] = useState(false);

  const [selectedSortBy, setSelectedSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleDetails = async (id: number) => {
    setMiniLoading(true);

    try {
      const request = await getRecieverFactoringRequestOnlyQuote(dispatch, {
        requestId: id,
      });
      setOpenRequest(request);
      setMiniLoading(false);
      setOpenShoppingCart(true);
    } catch (error) {
      setMiniLoading(false);
      return alert('error');
    }
  };

  const unAssignVerifier = async (id: number) => {
    try {
      setLoading(true);
      await unAssignVerifierToOperations(dispatch, {
        requestIds: [id],
      });
      await setUp();
      showAlert(dispatch, 'success', 'Verificador desasignado correctamente');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al desasignar verificador');
      setLoading(false);
    }
  };

  const canShowOpenDetails = (id: number) => {
    const request = factoringRequests.find((x: any) => x.id === id);
    if (!request) return alert('Ha ocurrido un error');

    return true;
  };

  const canShowUnassignVerifier = (id: number) => {
    const request = factoringRequests.find((x: any) => x.id === id);
    if (!request) return alert('Ha ocurrido un error');

    return request.verifierName !== 'Sin verificador';
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Verificar',
      canShow: canShowOpenDetails,
    },
    {
      action: unAssignVerifier,
      actionLabel: 'Desasignar',
      canShow: canShowUnassignVerifier,
    },
  ];

  const createQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    let query = '';
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    if (searchStatus) query += `status=${searchStatus}&`;
    if (searchVerified) query += `verified=${searchVerified}&`;
    if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (searchVerifierId && searchVerifierId !== 'all')
      query += `verifierId=${searchVerifierId}&`;
    if (searchBusinessIdentifier)
      query += `businessIdentifier=${searchBusinessIdentifier}&`;
    if (searchFactoringRequestId)
      query += `requestId=${searchFactoringRequestId}&`;
    if (exportComplete) query += `exportComplete=true&`;
    return query;
  };

  const searchData = async ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    setLoading(true);
    const query = createQuery({ page, pageSize, exportComplete });
    const factoringRequestsResponse = await fetchAllVerificationsByOperation(
      dispatch,
      query,
    );
    if (!exportComplete) {
      setFactoringRequests(factoringRequestsResponse.data || []);
      setPagination(factoringRequestsResponse.pagination);
    }
    setLoading(false);
    return factoringRequestsResponse;
  };

  const setUp = async (id?: number) => {
    setOpenShoppingCart(false);
    await searchData({ page: 1, pageSize: PAGE_SIZE, exportComplete: false });
    setLoading(false);
    if (id) {
      handleDetails(id);
    }
  };

  useEffect(() => {
    setUp();
  }, []);

  useEffect(() => {
    const canReadAllCheck = validatePermissions(user, 'verifications_read_all');
    setCanReadAll(canReadAllCheck);
  }, [user]);

  const fetchPortfolio = async () => {
    const portfolio = await fetchVerificationsPortfolio(dispatch);
    setUnassignedOperations(portfolio.data);
    setExecutivesOptions(portfolio.executivesOptions);
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = factoringRequests.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  useEffect(() => {
    if (canReadAll) void fetchPortfolio();
  }, [canReadAll]);

  const filters: Filter[] = [
    {
      label: 'Fecha de solicitud',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de solicitud',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      type: 'select' as const,
      label: 'Estado',
      value: searchStatus,
      setter: setSearchStatus,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Creada', value: 'CREATED' },
        { label: 'En revisión', value: 'IN_REVIEW' },
        { label: 'Aceptada', value: 'ACCEPTED' },
        { label: 'En giro', value: 'IN_TRANSFER' },
        { label: 'Rechazada', value: 'REJECTED' },
        { label: 'Activa', value: 'ACTIVE' },
        // { label: 'Completada', value: 'COMPLETED' },
      ],
    },
    ...(canReadAll
      ? [
          {
            type: 'select' as const,
            label: 'Verificador',
            value: searchVerifierId,
            setter: setSearchVerifierId,
            options: [
              { label: 'Todos', value: 'all' },
              ...executivesOptions.map((x: any) => ({
                label: x.name,
                value: x.id,
              })),
            ],
          },
        ]
      : []),
    {
      type: 'select' as const,
      label: 'Solicitud Verificada',
      value: searchVerified,
      setter: setSearchVerified,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Verificada', value: 'verified' },
        { label: 'No verificada', value: 'notVerified' },
      ],
    },
    {
      type: 'text' as const,
      label: 'ID Solicitud',
      value: searchFactoringRequestId,
      setter: setSearchFactoringRequestId,
      specialHandler: 'id',
    },
    {
      type: 'text' as const,
      label: 'Rut Cliente',
      value: searchBusinessIdentifier,
      setter: setSearchBusinessIdentifier,
    },
  ];
  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => setUp()}
          selectedSortBy={selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          sortOptions={[
            { value: 'createdAt', label: 'Creado en' },
            { value: 'amount', label: 'Monto' },
            { value: 'id', label: 'ID' },
          ]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
        />
      ),
    },
  ];

  const headers = [
    { key: 'id', label: 'ID', type: 'numeric-id' },
    ...(canReadAll ? [{ key: 'verifierName', label: 'Verificador' }] : []),
    {
      key: 'businessName',
      label: 'Cliente',
      handleAction: handleShowBusinessDetails,
      icon: <Info fontSize="small" />,
    },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'createdAt', label: 'Creado en', type: 'date-large' },
    {
      key: 'allDocumentsConfirmed',
      label: 'Doc. verificados',
      type: 'documents-confirmed-light',
    },
    { key: 'status', label: 'Estado', type: 'status' },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        variant={'body'}
        buttons={buttonsData}
        customTitle={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Typography>
              Acá podrás ver las solicitudes de factoring y sus verificaciones.
            </Typography>
            {validatePermissions(user, 'verifications_assign_verifier') &&
              unassignedOperations.length > 0 &&
              executivesOptions.length > 0 && (
                <Typography
                  onClick={() => {
                    setOpenAssignationDialog(true);
                  }}
                  style={{ cursor: 'pointer' }}
                  color="primary"
                  fontWeight={600}
                >
                  [{unassignedOperations.length} sin verificador asignado]
                </Typography>
              )}
          </div>
        }
      />
      <TableWithPagination
        dataHeaders={headers}
        data={factoringRequests}
        setData={setFactoringRequests}
        searchData={searchData}
        pagination={pagination}
        setPagination={setPagination}
        rowsPerPageDefault={25}
        showActions
        actions={actions}
        lastElement
        exportToFile="xlsx"
        exportComplete
        setLoading={setLoading}
      />
      {miniLoading && <CenteredCircularProgress type="layout" />}
      {openShoppingCart && (
        <ShoppingDialogRecieved
          openShoppingCart={openShoppingCart}
          setOpenShoppingCart={setOpenShoppingCart}
          selectedRequest={openRequest}
          setUp={setUp}
        />
      )}
      {openAssignationDialog && (
        <AssignationDialog
          operationsWithNoVerifier={unassignedOperations || []}
          executivesOptions={executivesOptions || []}
          openAssignationDialog={openAssignationDialog}
          setOpenAssignationDialog={setOpenAssignationDialog}
          setUp={setUp}
          fetchPortfolio={fetchPortfolio}
        />
      )}
    </div>
  );
};

export default VerificationByOperation;
